import React, { useContext, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useStaticQuery, graphql } from 'gatsby'
import { useTranslation, Trans } from 'gatsby-plugin-react-i18next'
import { GatsbyImage } from 'gatsby-plugin-image'
import ConditionalCTA from 'src/components/ConditionalCTA'
import Flag from 'react-world-flags'
import Analytics from 'src/utils/Analytics'

import {
  PLUS_PRODUCT_HANDLE,
  COUNTRIES,
  IS_FOREIGN_COUNTRY,
  SUPPORT_LINK,
  BUY_LINK,
  CAL_BUY_LINK,
  LOCALIZATION,
  IN_AU,
} from 'src/utils/constants'

import {
  Container,
  NavContainer,
  NavLink,
  NavA,
  HomeLink,
  DesktopCartLink,
  IconContainer,
  NavBuyNowLink,
  NavBuyNowAnchor,
  LogoContainer,
  MobileNavLinkContainer,
  QuantityText,
  QuantityTextMobile,
  MobileNavLinkA,
  MobileCartView,
  ButtonContainer,
  Text,
  MobileLink,
  MobileProduct,
  ProductsLink,
  HideOnMobile,
  ProductContainer,
  ProductCard,
  MobileCta,
  MobileText,
  ContactButton,
  SupportTitle,
  SupportText,
  SupportContainer,
  CalloutBubble,
  Callout,
  LinkContainer,
} from './styles'
import StoreContext from '../../context/StoreContext'

const useMenuState = () => {
  const [showMenu, setShowMenu] = useState(false)
  const [showCountries, setShowCountries] = useState(false)
  const [showFrameProducts, setShowFrameProducts] = useState(false)
  const [showCalendarProducts, setShowCalendarProducts] = useState(false)
  const [showSupportDetails, setShowSupportDetails] = useState(false)

  useEffect(() => {
    const { body } = document
    if (showMenu) {
      body.style.overflow = 'hidden'
      body.style.position = 'fixed'
      body.style.width = '100%'
      Analytics.track('Clicked Mobile Menu Burger CTA')
    } else {
      body.style.overflow = 'auto'
      body.style.position = 'relative'
    }
    return () => {
      body.style.overflow = 'auto'
      body.style.position = 'relative'
    }
  }, [showMenu])

  return {
    showMenu,
    setShowMenu,
    showFrameProducts,
    setShowFrameProducts,
    showCalendarProducts,
    setShowCalendarProducts,
    showSupportDetails,
    setShowSupportDetails,
    showCountries,
    setShowCountries,
  }
}

const useQuantity = () => {
  const {
    store: { cart },
  } = useContext(StoreContext)
  const { totalQuantity } = cart
  return [totalQuantity > 0, totalQuantity]
}

const Navigation = ({ buyLink, page, buyAnalyticsEvent, handle }) => {
  const { t } = useTranslation('common')
  const [hasItemsInCart, numItems] = useQuantity()
  const [dynamicPadding, setDynamicPadding] = useState(140)
  const {
    showMenu,
    setShowMenu,
    showFrameProducts,
    setShowFrameProducts,
    showCalendarProducts,
    setShowCalendarProducts,
    showSupportDetails,
    setShowSupportDetails,
    showCountries,
    setShowCountries,
  } = useMenuState()

  const {
    logoBlue,
    mobileLogoBlue,
    cartIcon,
    mobileCartIcon,
    burgerIcon,
    closeIcon,
    arrowRightIcon,
    arrowDownIcon,
    arrowUpIcon,
    frameProduct,
    frameProductUSA,
    calendarProduct,
    calendarProductUSA,
  } = useStaticQuery(graphql`
    query NavImagesQuery {
      logoBlue: file(relativePath: { eq: "skylight-logo.png" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      mobileLogoBlue: file(relativePath: { eq: "skylight-icon.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      cartIcon: file(relativePath: { eq: "icons/cart-icon.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, width: 25, layout: FIXED, placeholder: NONE)
        }
      }
      mobileCartIcon: file(relativePath: { eq: "icons/cart-icon.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, width: 32, layout: FIXED, placeholder: NONE)
        }
      }
      burgerIcon: file(relativePath: { eq: "icons/burger-icon.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, width: 28, layout: FIXED, placeholder: NONE)
        }
      }
      closeIcon: file(relativePath: { eq: "icons/close-icon.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, width: 28, layout: FIXED, placeholder: NONE)
        }
      }
      arrowRightIcon: file(relativePath: { eq: "icons/right-arrow.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      arrowDownIcon: file(relativePath: { eq: "icons/down-arrow.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      arrowUpIcon: file(relativePath: { eq: "icons/up-arrow.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      frameProductUSA: file(relativePath: { eq: "frame-products.jpg" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      frameProduct: file(relativePath: { eq: "nav-frame.jpg" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      calendarProductUSA: file(relativePath: { eq: "calendar-products.jpg" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      calendarProduct: file(relativePath: { eq: "nav-cal.jpg" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
    }
  `)

  useEffect(() => {
    setTimeout(() => {
      const banner = document.querySelector('.banner')
      if (banner) {
        setDynamicPadding(200)
      } else {
        setDynamicPadding(140)
      }
    }, 0)
  }, [])

  const RightArrow = (
    <IconContainer>
      <GatsbyImage image={arrowRightIcon.childImageSharp.gatsbyImageData} alt="Right Arrow" />
    </IconContainer>
  )
  const DownArrow = (
    <IconContainer>
      <GatsbyImage image={arrowDownIcon.childImageSharp.gatsbyImageData} alt="Down Arrow" />
    </IconContainer>
  )
  const UpArrow = (
    <IconContainer>
      <GatsbyImage image={arrowUpIcon.childImageSharp.gatsbyImageData} alt="Up Arrow" />
    </IconContainer>
  )

  const HomepageLink = (
    <HomeLink to="/">
      <LogoContainer>
        <GatsbyImage image={logoBlue.childImageSharp.gatsbyImageData} alt="Skylight Logo" />
        <GatsbyImage image={mobileLogoBlue.childImageSharp.gatsbyImageData} alt="Skylight Logo" />
      </LogoContainer>
    </HomeLink>
  )

  const FlagDropDown = () => (
    <div className="dropdown">
      <button
        type="button"
        className="countryDropbtn"
        aria-label={`${COUNTRIES[LOCALIZATION]?.abv} Flag`}
      >
        <Flag
          alt={`${COUNTRIES[LOCALIZATION]?.country} Flag`}
          className="flagShadow"
          code={COUNTRIES[LOCALIZATION]?.abv}
          height="20"
        />
      </button>
      <div className="country-dropdown-content">
        {Object.values(COUNTRIES).map((country, i) => {
          if (country.localization !== LOCALIZATION) {
            return (
              <NavA key={i} href={country.link} target="_blank" rel="noopener noreferrer">
                <Flag
                  alt={`${country.country} Flag`}
                  className="flagShadow"
                  code={country.abv}
                  height="18"
                />
              </NavA>
            )
          }
          return null
        })}
      </div>
    </div>
  )

  const frameProductLinks = [
    { link: '/frame/', text: 'Frame' },
    { link: '/frame-2/', text: 'Frame 2' },
    { link: '/products/snap-frame/', text: 'Accessories' },
  ]
  const calendarProductLinks = [
    { link: '/calendar/', text: 'Calendar' },
    { link: '/calendar-max', text: 'Cal Max' },
  ]

  let supportLink
  if (handle?.includes('calendar') || page?.includes('calendar')) {
    supportLink = SUPPORT_LINK.calendar
  } else {
    supportLink = SUPPORT_LINK[LOCALIZATION]
  }

  const AUNavLinks = [
    { link: supportLink, text: 'Support' },
    { link: 'https://app.ourskylight.com/download-app', text: 'Log In' },
  ]
  const navlinks = IN_AU
    ? AUNavLinks
    : [{ link: 'https://app.ourskylight.com/download-app', text: 'Log In' }]

  const contactLinks = [
    {
      label: 'Contact Us',
      href: 'https://skylight.zendesk.com/hc/en-us/categories/360001842931-Contact-Us',
    },
    {
      label: 'Join a Live Calendar Demo',
      href: 'https://calendly.com/skylightconcierge/skylightunboxed',
    },
    {
      label: 'Help Center',
      href: 'https://skylight.zendesk.com/hc/en-us',
    },
  ]

  const links = IS_FOREIGN_COUNTRY
    ? [
        contactLinks.find((link) => link.label === 'Help Center'),
        contactLinks.find((link) => link.label === 'Join a Live Calendar Demo'),
      ]
    : contactLinks

  const productDetails = [
    {
      name: 'Shop Frame',
      image: IS_FOREIGN_COUNTRY ? frameProduct : frameProductUSA,
      link: BUY_LINK,
    },
    {
      name: 'Shop Calendar',
      image: IS_FOREIGN_COUNTRY ? calendarProduct : calendarProductUSA,
      link: CAL_BUY_LINK,
    },
  ]

  const SupportDetails = () => (
    <>
      <SupportTitle>We've Got Your Back</SupportTitle>
      <SupportText>
        Our team is available to you 24 hours a day, 7 days a week, 365 days a year. In other words,
        whenever you need us!
      </SupportText>
      {links.map(({ label, href }) => (
        <ContactButton
          key={label}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => Analytics.track(`Clicked Support: ${label} CTA`)}
        >
          {label}
        </ContactButton>
      ))}
    </>
  )

  return (
    <>
      <Container>
        {HomepageLink}
        <NavContainer>
          <HideOnMobile>
            <div className="dropdown">
              <button type="button" className="dropbtn">
                <Trans>Shop Frame</Trans>
              </button>
              <div className="dropdown-content">
                {frameProductLinks
                  .filter(
                    ({ text }) =>
                      !IS_FOREIGN_COUNTRY || (text !== 'Accessories' && text !== 'Frame 2')
                  )
                  .map((product, idx) => (
                    <NavLink
                      to={product.link}
                      key={`${product.text}-${idx}`}
                      onClick={() => Analytics.track(`Clicked Shop ${product.text} CTA`)}
                    >
                      <Trans>{product.text}</Trans>
                    </NavLink>
                  ))}
              </div>
            </div>
          </HideOnMobile>
          <HideOnMobile>
            <div className="dropdown">
              <button type="button" className="dropbtn">
                <Trans>Shop Calendar</Trans>
              </button>
              <div className="dropdown-content">
                {calendarProductLinks.map((product, idx) => (
                  <NavLink
                    to={product.link}
                    key={`${product.text}-${idx}`}
                    onClick={() => Analytics.track(`Clicked Shop ${product.text} CTA`)}
                  >
                    <LinkContainer>
                      <Trans>{product.text}</Trans>
                      {IS_FOREIGN_COUNTRY && product.text === 'Cal Max' && (
                        <CalloutBubble>
                          <Callout>New</Callout>
                        </CalloutBubble>
                      )}
                    </LinkContainer>
                  </NavLink>
                ))}
              </div>
            </div>
          </HideOnMobile>
          {!IN_AU && (
            <HideOnMobile>
              <div className="dropdown">
                <button type="button" className="dropbtn">
                  <Trans>Support</Trans>
                </button>
                <div className="support-dropdown-content">
                  <SupportDetails />
                </div>
              </div>
            </HideOnMobile>
          )}
          {navlinks.map((link, idx) => (
            <NavA
              href={link.link}
              target="_blank"
              rel="noopener noreferrer"
              key={`${link.text} - ${idx}`}
            >
              <Trans>{link.text}</Trans>
            </NavA>
          ))}
          <IconContainer onClick={() => setShowMenu(!showMenu)} noMargin={showMenu}>
            <GatsbyImage
              image={
                showMenu
                  ? closeIcon.childImageSharp.gatsbyImageData
                  : burgerIcon.childImageSharp.gatsbyImageData
              }
              alt="Menu"
            />
          </IconContainer>
          {!showMenu && (
            <>
              <FlagDropDown />
              <DesktopCartLink to="/cart/">
                <GatsbyImage image={cartIcon.childImageSharp.gatsbyImageData} alt="Shopping Cart" />
                {hasItemsInCart && <QuantityText>{numItems}</QuantityText>}
              </DesktopCartLink>
            </>
          )}
          {showMenu || !!handle ? null : (
            <ConditionalCTA
              url={buyLink}
              text={t('Buy Now ')}
              analyticsEvent={buyAnalyticsEvent}
              anchorComponent={NavBuyNowAnchor}
              linkComponent={NavBuyNowLink}
              page={page}
            />
          )}
        </NavContainer>
      </Container>
      {/* Mobile Menu */}
      {showMenu && (
        <MobileNavLinkContainer showMenu={showMenu} dynamicPadding={dynamicPadding}>
          <ProductContainer>
            {productDetails.map((product, idx) => (
              <ProductCard
                to={product.link}
                index={idx}
                key={product.name}
                onClick={() => {
                  setShowMenu(false)
                  Analytics.track(`Clicked Mobile Menu ${product.name} CTA`)
                }}
              >
                <GatsbyImage
                  image={product.image.childImageSharp.gatsbyImageData}
                  alt={product.name}
                />
                <ButtonContainer
                  display={!showMenu && handle === `${PLUS_PRODUCT_HANDLE}` ? 'none' : 'flex'}
                >
                  <MobileCta>
                    <MobileText>{t(product.name)}</MobileText>
                  </MobileCta>
                </ButtonContainer>
              </ProductCard>
            ))}
          </ProductContainer>
          <ProductsLink onClick={() => setShowFrameProducts(!showFrameProducts)}>
            <Text>Shop Frame</Text>
            {showFrameProducts ? UpArrow : DownArrow}
          </ProductsLink>
          {showFrameProducts && (
            <>
              {frameProductLinks
                .filter(
                  ({ text }) =>
                    !IS_FOREIGN_COUNTRY || (text !== 'Accessories' && text !== 'Frame 2')
                )
                .map((product, idx) => (
                  <MobileLink
                    to={product.link}
                    onClick={() => {
                      setShowMenu(false)
                      Analytics.track(`Clicked Mobile Menu Shop ${product.text} CTA`)
                    }}
                    key={`${product.text} - ${idx}`}
                  >
                    <MobileProduct>
                      <Trans>{product.text}</Trans>
                    </MobileProduct>
                  </MobileLink>
                ))}
            </>
          )}
          <ProductsLink onClick={() => setShowCalendarProducts(!showCalendarProducts)}>
            <Text>Shop Calendar</Text>
            {showCalendarProducts ? UpArrow : DownArrow}
          </ProductsLink>
          {showCalendarProducts && (
            <>
              {calendarProductLinks.map((product, idx) => (
                <>
                  <MobileLink
                    to={product.link}
                    onClick={() => {
                      setShowMenu(false)
                      Analytics.track(`Clicked Mobile Menu Shop ${product.text} CTA`)
                    }}
                    key={`${product.text} - ${idx}`}
                  >
                    <p>
                      <Trans>{product.text}</Trans>
                    </p>
                  </MobileLink>
                  {IS_FOREIGN_COUNTRY && product.text === 'Cal Max' && (
                    <CalloutBubble>
                      <Callout>New</Callout>
                    </CalloutBubble>
                  )}
                </>
              ))}
            </>
          )}
          {!IN_AU && (
            <>
              <ProductsLink onClick={() => setShowSupportDetails(!showSupportDetails)}>
                <Text>Support</Text>
                {showSupportDetails ? UpArrow : DownArrow}
              </ProductsLink>
              {showSupportDetails && (
                <SupportContainer>
                  <SupportDetails />
                </SupportContainer>
              )}
            </>
          )}
          {navlinks.map((link, idx) => (
            <MobileNavLinkA
              href={link.link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setShowMenu(false)}
              key={`${link.text} - ${idx}`}
            >
              <Text>
                <Trans>{link.text}</Trans>
              </Text>
              {RightArrow}
            </MobileNavLinkA>
          ))}
          <ProductsLink onClick={() => setShowCountries(!showCountries)}>
            <Text>
              <Flag
                alt={`${COUNTRIES[LOCALIZATION]?.country} Flag`}
                className="flagShadow"
                code={COUNTRIES[LOCALIZATION]?.abv}
                height="18"
              />{' '}
              <Trans>{COUNTRIES[LOCALIZATION]?.country}</Trans>
            </Text>
            {showCountries ? UpArrow : DownArrow}
          </ProductsLink>
          {showCountries && (
            <>
              {Object.values(COUNTRIES).map((country, i) => {
                if (country.localization !== LOCALIZATION) {
                  return (
                    <MobileNavLinkA
                      key={i}
                      href={country.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setShowCountries(false)}
                    >
                      <MobileProduct>
                        <Flag
                          alt={`${country.country} Flag`}
                          className="flagShadow"
                          code={country.abv}
                          width="30"
                        />
                        <Trans>{country.country}</Trans>
                      </MobileProduct>
                    </MobileNavLinkA>
                  )
                }
                return null
              })}
            </>
          )}
          <MobileLink to="/cart/" onClick={() => setShowMenu(false)}>
            <MobileCartView>
              <GatsbyImage
                image={mobileCartIcon.childImageSharp.gatsbyImageData}
                alt="Shopping Cart"
              />
              {hasItemsInCart && <QuantityTextMobile>{numItems}</QuantityTextMobile>}
              <p>
                <Trans>Shopping Cart</Trans>
              </p>
            </MobileCartView>
          </MobileLink>
        </MobileNavLinkContainer>
      )}
    </>
  )
}

Navigation.propTypes = {
  buyLink: PropTypes.string.isRequired,
  page: PropTypes.string,
  buyAnalyticsEvent: PropTypes.string,
  handle: PropTypes.string,
}

export default Navigation
